import React from 'react';
import PropTypes from 'prop-types';
import { DialogOverlay, DialogContent } from '@reach/dialog';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import colors from '../colors';
import Button from '../Button';

import styles from './styles.module.scss';

//
// ModalOverlay
//
// Reach UI docs: https://reach.tech/dialog/#dialogoverlay
// --------------------

export function ModalOverlay({ className, children, ...otherProps }) {
  const classNames = [styles.overlay];
  if (className) {
    classNames.push(className);
  }
  return (
    <DialogOverlay className={classNames.join(' ')} {...otherProps}>
      {children}
    </DialogOverlay>
  );
}

// Override DialogContent display name so that error from prop types show 'ModalOverlay'
DialogOverlay.displayName = 'ModalOverlay';

ModalOverlay.defaultProps = {
  className: null,
  isOpen: false,
  onDismiss: () => {},
  initialFocusRef: () => {},

  allowPinchZoom: false,
  dangerouslyBypassFocusLock: false,
  dangerouslyBypassScrollLock: false
};

ModalOverlay.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onDismiss: PropTypes.func,
  initialFocusRef: PropTypes.func,
  allowPinchZoom: PropTypes.bool,
  dangerouslyBypassFocusLock: PropTypes.bool,
  dangerouslyBypassScrollLock: PropTypes.bool
};

//
// ModalContent
//
// Reach UI docs: https://reach.tech/dialog/#dialogcontent
// --------------------

export function ModalContent({ className, children, ...otherProps }) {
  const classNames = [styles.dialog];
  if (className) {
    classNames.push(className);
  }
  return (
    <DialogContent className={classNames.join(' ')} {...otherProps}>
      {children}
    </DialogContent>
  );
}

// Override DialogContent display name so that error from prop types show 'ModalContent'
DialogContent.displayName = 'ModalContent';

ModalContent.defaultProps = {
  className: null
};

ModalContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  //
  // @reach/dialog will handle prop-type validation for us, but in order for this
  // props to show up in Storybook we need to add them here.
  //
  /* eslint-disable react/require-default-props */
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string
  /* eslint-enable react/require-default-props */
};

//
// ModalTitle
// --------------------

export function ModalTitle({ className, children, ...otherProps }) {
  const classNames = [styles.title];
  if (className) {
    classNames.push(className);
  }
  return (
    <h2 className={classNames.join(' ')} {...otherProps}>
      {children}
    </h2>
  );
}

ModalTitle.defaultProps = {
  className: null
};

ModalTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

//
// ModalActions
// --------------------

export function ModalActions({ className, children, ...otherProps }) {
  const classNames = [styles.actions];
  if (className) {
    classNames.push(className);
  }
  return (
    <div className={classNames.join(' ')} {...otherProps}>
      {children}
    </div>
  );
}

ModalActions.defaultProps = {
  className: null
};

ModalActions.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

//
// ModalHeroImage
// --------------------

export function ModalHeroImage({ className, src, alt, ...otherProps }) {
  const classNames = [styles.heroImage];
  if (className) {
    classNames.push(className);
  }
  return (
    <img src={src} alt={alt} className={classNames.join(' ')} {...otherProps} />
  );
}

ModalHeroImage.defaultProps = {
  className: null
};

ModalHeroImage.propTypes = {
  className: PropTypes.string,
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired
};

//
// ModalBody
// --------------------

export function ModalBody({ className, ...otherProps }) {
  const classNames = [styles.body];
  if (className) {
    classNames.push(className);
  }
  return <div className={classNames.join(' ')} {...otherProps} />;
}

ModalBody.defaultProps = {
  className: null
};

ModalBody.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};

//
// ModalCloseButton
// --------------------

export function ModalCloseButton({ className, color, ...otherProps }) {
  return (
    <Button type="button" variant="icon" className={className} {...otherProps}>
      <ActionsCancelNormalXsmall fill={color || colors.BLACK} />
    </Button>
  );
}

ModalCloseButton.defaultProps = {
  className: null,
  color: null
};

ModalCloseButton.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string
};

//
// Modal
// --------------------

function Modal({
  className,
  isOpen,
  onDismiss,
  hasClose,
  children,
  initialFocusRef,
  ...otherProps
}) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onDismiss={onDismiss}
      initialFocusRef={initialFocusRef}
    >
      <ModalContent className={className} {...otherProps}>
        {hasClose && (
          <ModalCloseButton
            className={styles.closeButton}
            onClick={onDismiss}
          />
        )}
        {children}
      </ModalContent>
    </ModalOverlay>
  );
}

Modal.defaultProps = {
  className: null,
  isOpen: false,
  hasClose: false,
  onDismiss: () => {},
  initialFocusRef: () => {}
};

Modal.propTypes = {
  /**
   * This style will be applied to ModalContent used by Modal component
   */
  className: PropTypes.string,

  /**
   * Whether modal is open or closed - aka visitable to the user
   */
  isOpen: PropTypes.bool,

  /**
   * Called when modal will be closed
   */
  onDismiss: PropTypes.func,

  /**
   * By default the first focusable element will receive focus when the dialog opens but you can provide a ref to focus instead.
   */
  initialFocusRef: PropTypes.func,

  /**
   * Whether to show close button or not
   */
  hasClose: PropTypes.bool,

  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,

  //
  // @reach/dialog will handle prop-type validation for us, but in order for this
  // props to show up in Storybook we need to add them here.
  //
  /* eslint-disable react/require-default-props */
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string
  /* eslint-enable react/require-default-props */
};

export default Modal;
